import { useCallback, useEffect, useMemo, useState } from "react";
import { resolveTemplateLayoutRepositorySelection } from "@/lib/template-studio/repository/getTemplateLayoutRepository";
import type {
  TemplateLayoutRepository,
  TemplateLayoutRepositoryContext,
} from "@/lib/template-studio/repository/templateLayoutRepository.types";
import useAuth, { type AuthState } from "@/store/auth.slice";
import type {
  TemplateLayout,
  TemplateLayoutStatus,
  TemplateLayoutSummary,
} from "@/types/template-layout/templateLayout.types";

export const TEMPLATE_LAYOUT_LIST_REQUEST_ERROR_MESSAGE =
  "Templates couldn't be loaded. Please try again.";

export interface TemplateLayoutListError {
  kind: "request";
  message: string;
}

export interface UseTemplateLayoutListOptions {
  repository?: TemplateLayoutRepository;
  context?: TemplateLayoutRepositoryContext;
}

export interface UseTemplateLayoutListReturn {
  items: TemplateLayoutSummary[];
  isLoading: boolean;
  error: TemplateLayoutListError | null;
  isRetryable: boolean;
  retry: () => void;
  /** Updates one template's status in the list and its cache, then refetches. */
  setItemStatus: (id: string, status: TemplateLayoutStatus) => void;
}

const templateLayoutListCache = new WeakMap<
  TemplateLayoutRepository,
  Map<string, TemplateLayoutSummary[]>
>();

const getCacheKey = (context: TemplateLayoutRepositoryContext) =>
  JSON.stringify([context.tenantId, context.userId]);

const getCachedItems = (
  repository: TemplateLayoutRepository,
  cacheKey: string,
) => templateLayoutListCache.get(repository)?.get(cacheKey);

const cacheItems = (
  repository: TemplateLayoutRepository,
  cacheKey: string,
  items: TemplateLayoutSummary[],
) => {
  const repositoryCache =
    templateLayoutListCache.get(repository) ??
    new Map<string, TemplateLayoutSummary[]>();
  repositoryCache.set(cacheKey, items);
  templateLayoutListCache.set(repository, repositoryCache);
};

export const upsertCachedTemplateLayout = (
  repository: TemplateLayoutRepository,
  context: TemplateLayoutRepositoryContext,
  template: TemplateLayout,
) => {
  const cacheKey = getCacheKey(context);
  const cachedItems = getCachedItems(repository, cacheKey);
  if (cachedItems === undefined) return;

  const { document: _document, ...summary } = template;

  cacheItems(repository, cacheKey, [
    summary,
    ...cachedItems.filter((item) => item.id !== summary.id),
  ]);
};

/**
 * Loads template layout summaries for the templates landing page.
 * @public
 */
export const useTemplateLayoutList = ({
  repository: repositoryOverride,
  context: contextOverride,
}: UseTemplateLayoutListOptions = {}): UseTemplateLayoutListReturn => {
  const userId = useAuth((state: AuthState) => state.user.id);
  const tenantId = useAuth((state: AuthState) => state.user.default_tenant.id);
  const selection = useMemo(
    () =>
      resolveTemplateLayoutRepositorySelection({
        user: { id: userId, default_tenant: { id: tenantId } },
        repository: repositoryOverride,
        context: contextOverride,
      }),
    [contextOverride, repositoryOverride, tenantId, userId],
  );
  const cacheKey = getCacheKey(selection.context);
  const cachedItems = getCachedItems(selection.repository, cacheKey);
  const [items, setItems] = useState<TemplateLayoutSummary[]>(
    () => cachedItems ?? [],
  );
  const [isLoading, setIsLoading] = useState(cachedItems === undefined);
  const [error, setError] = useState<TemplateLayoutListError | null>(null);
  const [stateRepository, setStateRepository] = useState(selection.repository);
  const [stateCacheKey, setStateCacheKey] = useState(cacheKey);
  const [fetchGeneration, setFetchGeneration] = useState(0);

  const retry = useCallback(() => {
    if (error?.kind !== "request") return;
    setFetchGeneration((generation) => generation + 1);
  }, [error]);

  const setItemStatus = useCallback(
    (id: string, status: TemplateLayoutStatus) => {
      const withStatus = (current: TemplateLayoutSummary[]) =>
        current.map((item) => (item.id === id ? { ...item, status } : item));
      setItems(withStatus);
      const cached = getCachedItems(selection.repository, cacheKey);
      if (cached !== undefined) {
        cacheItems(selection.repository, cacheKey, withStatus(cached));
      }
      // Restarts any in-flight load so a pre-change response can't overwrite this.
      setFetchGeneration((generation) => generation + 1);
    },
    [cacheKey, selection.repository],
  );

  useEffect(() => {
    let cancelled = false;
    const cachedItems = getCachedItems(selection.repository, cacheKey);

    const finishWithError = (nextError: TemplateLayoutListError) => {
      if (cancelled) return;
      if (cachedItems === undefined) {
        setItems([]);
      }
      setError(nextError);
      setIsLoading(false);
    };
    const finishWithRequestError = () => {
      finishWithError({
        kind: "request",
        message: TEMPLATE_LAYOUT_LIST_REQUEST_ERROR_MESSAGE,
      });
    };

    const load = async () => {
      setStateRepository(selection.repository);
      setStateCacheKey(cacheKey);
      if (cachedItems !== undefined) {
        setItems(cachedItems);
      } else {
        setItems([]);
      }
      setIsLoading(cachedItems === undefined);
      setError(null);

      const loadedItems: TemplateLayoutSummary[] = [];
      const seenCursors = new Set<string>();
      let after: string | undefined;

      try {
        while (true) {
          const listResult = await selection.repository.list(
            selection.context,
            after === undefined ? undefined : { after },
          );

          if (cancelled) return;

          if (!listResult.success) {
            finishWithRequestError();
            return;
          }

          loadedItems.push(...listResult.data.items);
          if (!listResult.data.pageInfo.hasNextPage) break;

          const nextCursor = listResult.data.pageInfo.endCursor;
          if (nextCursor === null || seenCursors.has(nextCursor)) {
            finishWithRequestError();
            return;
          }

          seenCursors.add(nextCursor);
          after = nextCursor;
        }
      } catch {
        finishWithRequestError();
        return;
      }

      cacheItems(selection.repository, cacheKey, loadedItems);
      setItems(loadedItems);
      setError(null);
      setIsLoading(false);
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [cacheKey, fetchGeneration, selection]);

  const stateMatchesSelection =
    stateRepository === selection.repository && stateCacheKey === cacheKey;
  const visibleItems = stateMatchesSelection ? items : (cachedItems ?? []);
  const visibleIsLoading = stateMatchesSelection
    ? isLoading
    : cachedItems === undefined;
  const visibleError = stateMatchesSelection ? error : null;
  const isRetryable = visibleError?.kind === "request";

  return {
    items: visibleItems,
    isLoading: visibleIsLoading,
    error: visibleError,
    isRetryable,
    retry,
    setItemStatus,
  };
};
